<template>
  <div>
    <NamiButton
      class="whitespace-nowrap"
      button-type="primary"
      block
      @button-click="tryOpenModal"
      :icon="IconPlaylistAdd"
      :disabled="pending"
      >Add to List</NamiButton
    >
    <NamiModal v-model="modalOpen">
      <NamiModalTitle @close="modalOpen = false"
        >Add Title to List</NamiModalTitle
      >
      <div>
        <NamiLoading v-if="pending || addedPending" size="small" />
        <template v-else>
          <div class="flex flex-col mb-4">
            <NamiCheckbox
              v-for="(list, id) in data"
              :model-value="listsAdded[id]"
              @update:model-value="(v) => (listsAdded[id] = v)"
              :label="list.attributes.title"
            />
          </div>
          <TheNuxtLink
            :href="
              href.list.create + `?returnTo=${route.path}&addId=${titleId}`
            "
          >
            <NamiEmptyState class="mb-4"
              ><IconPlus class="mr-2" />Create New List</NamiEmptyState
            >
          </TheNuxtLink>
        </template>
      </div>
      <div class="flex justify-end gap-4">
        <NamiButton
          button-type="secondary"
          @button-click="modalOpen = false"
          :disabled="updatePending"
          >Cancel</NamiButton
        >
        <NamiButton
          button-type="primary"
          @button-click="updateLists"
          :loading="updatePending"
          :disabled="updatePending"
          >Confirm</NamiButton
        >
      </div>
    </NamiModal>
  </div>
</template>

<script setup lang="ts">
import { TitleList } from "~/src/api";
import { IconPlaylistAdd, IconPlus } from "@tabler/icons-vue";

const route = useRoute();
const nuxtApp = useNuxtApp();
const authStore = nuxtApp.$auth();
const appStore = nuxtApp.$app();
const loggedIn = useIsLoggedIn();
const href = useHref();
const userId = computed(() => authStore?.user?.profile.sub);

const props = defineProps<{
  titleId: string;
}>();

const modalOpen = ref(false);

const initialAddedMap = ref<Record<string, boolean>>({});

const {
  data: listsAdded,
  pending: addedPending,
  refresh,
} = useAsyncData(
  `user-list-added-${props.titleId}`,
  async () => {
    await authStore?.waitUntilRefreshIsComplete();
    if (!userId.value || !loggedIn.value) return;
    const token = await getTokenOrThrow();

    const res = await TitleList.find(
      {
        userId: userId.value,
        titleIds: [props.titleId],
        visibilities: ["private", "public"],
        showDeleted: false,
      },
      token,
    );
    initialAddedMap.value = Object.fromEntries(
      res.data.map((list) => [list.id, true]),
    );
    return JSON.parse(JSON.stringify(initialAddedMap.value));
  },
  { server: false, immediate: false },
);

const { data, pending } = useAsyncData(
  "user-title-lists",
  async () => {
    await authStore?.waitUntilRefreshIsComplete();
    if (!userId.value || !loggedIn.value) return {};
    const token = await getTokenOrThrow();

    const res = await TitleList.find(
      {
        userId: userId.value,
        visibilities: ["private", "public"],
        showDeleted: false,
      },
      token,
    );
    return Object.fromEntries(res.data.map((list) => [list.id, list]));
  },
  { server: false },
);

function tryOpenModal() {
  if (!loggedIn.value) return appStore?.openLoginRequiredModal();
  modalOpen.value = true;
  if (!listsAdded.value) refresh();
}

const { action: updateLists, pending: updatePending } = useAction2(async () => {
  const token = await getTokenOrThrow();

  const promises = Object.entries(listsAdded.value).map(([id, add]) => {
    if (initialAddedMap.value[id] !== add) {
      if (add)
        return TitleList.modifyTitlesInList(
          id,
          { add: [props.titleId] },
          token,
        );
      else
        return TitleList.modifyTitlesInList(
          id,
          { remove: [props.titleId] },
          token,
        );
    }
  });

  await Promise.all(promises);
  appStore?.notify({
    preset: "success.plain",
    detail: "Lists updated.",
    timer: 3000,
  });
  modalOpen.value = false;
  return;
});
</script>

<style scoped lang="postcss"></style>
